// Events composable — fetches, stores, and sorts all entries
import { ref, computed } from 'vue'
import type { Entry, Service, Component } from '@/types'
import { useAuth } from './useAuth'

const allEntries = ref<Entry[]>([])
const servicesList = ref<Service[]>([])
const componentsList = ref<Component[]>([])
const isLoading = ref(false)
const lastUpdated = ref<string | null>(null)

export function useEvents() {
  const { isAuthenticated } = useAuth()

  async function loadServices() {
    try {
      const res = await fetch('data/services.json')
      servicesList.value = await res.json()
    } catch {
      // Silently fail
    }
  }

  async function loadComponents() {
    try {
      const res = await fetch('data/components.json')
      componentsList.value = await res.json()
    } catch {
      // Silently fail
    }
  }

  async function loadEvents() {
    isLoading.value = true
    try {
      const apiUrl = isAuthenticated.value ? '/api/admin/events' : '/api/events'
      const res = await fetch(apiUrl, { cache: 'no-store' })
      if (!res.ok) throw new Error('Failed to load events')

      const payload = await res.json()
      const events = Array.isArray(payload) ? payload : payload.events
      if (!Array.isArray(events)) throw new Error('Invalid events response')

      allEntries.value = events
        .slice()
        .sort((a, b) => new Date(b.start_at).getTime() - new Date(a.start_at).getTime())
      lastUpdated.value = payload.lastUpdated || null
    } catch (err) {
      console.error('Load events error:', err)
      allEntries.value = []
    } finally {
      isLoading.value = false
    }
  }

  function findServiceByName(name: string): Service | undefined {
    if (!name) return undefined
    return servicesList.value.find((s) => s.name === name)
  }

  function findComponentByName(name: string): Component | undefined {
    if (!name) return undefined
    return componentsList.value.find((c) => c.name === name)
  }

  function getServiceLabel(service: Service | undefined, lang?: string): string {
    if (!service) return ''
    const l = lang || 'de'
    return service.translations?.[l]?.name || service.name || ''
  }

  function getComponentLabel(component: Component | undefined, lang?: string): string {
    if (!component) return ''
    const l = lang || 'de'
    return component.translations?.[l]?.name || component.name || ''
  }

  return {
    allEntries,
    servicesList,
    componentsList,
    isLoading,
    lastUpdated,
    loadEvents,
    loadServices,
    loadComponents,
    findServiceByName,
    findComponentByName,
    getServiceLabel,
    getComponentLabel,
  }
}
